import { supabase } from '../lib/supabase'

export type OverrideSource = 'inline_edit' | 'detail_page' | 'import' | 'bulk_update'

export interface OverrideHistoryEntry {
  id: string
  aircraft_id?: number
  transaction_id?: string
  field_name: string
  old_value: string | null
  new_value: string | null
  changed_at: string
  changed_by: string | null
  source: OverrideSource
}

export interface LogOverrideChangeParams {
  aircraftId?: number
  transactionId?: string
  fieldName: string
  oldValue: unknown
  newValue: unknown
  source?: OverrideSource
}

/**
 * Service for logging and retrieving override history.
 * Supports both ac_overrides_history and transaction_overrides_history tables.
 */
export class OverrideHistoryService {
  /**
   * Log a change to an aircraft override field
   */
  static async logAircraftOverrideChange(params: Omit<LogOverrideChangeParams, 'transactionId'> & { aircraftId: number }): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { error } = await supabase
        .from('ac_overrides_history')
        .insert({
          aircraft_id: params.aircraftId,
          field_name: params.fieldName,
          old_value: params.oldValue !== null && params.oldValue !== undefined 
            ? JSON.stringify(params.oldValue) 
            : null,
          new_value: params.newValue !== null && params.newValue !== undefined 
            ? JSON.stringify(params.newValue) 
            : null,
          changed_by: user?.id || null,
          source: params.source || 'inline_edit'
        })

      if (error) {
        throw error
      }
    } catch (error) {
      // Don't throw - history logging should not block the main operation
    }
  }

  /**
   * Log a change to a transaction override field
   */
  static async logTransactionOverrideChange(params: Omit<LogOverrideChangeParams, 'aircraftId'> & { transactionId: string }): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { error } = await supabase
        .from('transaction_overrides_history')
        .insert({
          transaction_id: params.transactionId,
          field_name: params.fieldName,
          old_value: params.oldValue !== null && params.oldValue !== undefined 
            ? JSON.stringify(params.oldValue) 
            : null,
          new_value: params.newValue !== null && params.newValue !== undefined 
            ? JSON.stringify(params.newValue) 
            : null,
          changed_by: user?.id || null,
          source: params.source || 'inline_edit'
        })

      if (error) {
        throw error
      }
    } catch (error) {
      // Don't throw - history logging should not block the main operation
    }
  }

  /**
   * Get history for a specific aircraft
   */
  static async getAircraftHistory(aircraftId: number): Promise<OverrideHistoryEntry[]> {
    const { data, error } = await supabase
      .from('ac_overrides_history')
      .select('*')
      .eq('aircraft_id', aircraftId)
      .order('changed_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  }

  /**
   * Get history for a specific transaction
   */
  static async getTransactionHistory(transactionId: string): Promise<OverrideHistoryEntry[]> {
    const { data, error } = await supabase
      .from('transaction_overrides_history')
      .select('*')
      .eq('transaction_id', transactionId)
      .order('changed_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  }

  /**
   * Get the most recent value for a specific field (for revert functionality)
   */
  static async getLastAircraftFieldValue(
    aircraftId: number, 
    fieldName: string
  ): Promise<{ oldValue: unknown; changedAt: string } | null> {
    const { data, error } = await supabase
      .from('ac_overrides_history')
      .select('old_value, changed_at')
      .eq('aircraft_id', aircraftId)
      .eq('field_name', fieldName)
      .order('changed_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      return null
    }

    if (!data) return null

    return {
      oldValue: data.old_value ? JSON.parse(data.old_value) : null,
      changedAt: data.changed_at
    }
  }
}

